<script setup lang="ts">
import { scorePassword } from '@/utils/general';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiPasswordMeterProps {
  text: string | undefined;
  title?: boolean;
}
const props = withDefaults(defineProps<CiPasswordMeterProps>(), {
  title: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const { $gettext } = useGettext();

/********************
 * REFS & VARS       *
 ********************/
const i18nGetPasswordLabel = computed(() => {
  const labels = [$gettext('Weak'), $gettext('Ok'), $gettext('Nice'), $gettext('Strong'), $gettext('Very Strong')];
  return labels[passwordStrength.value];
});

const passwordStrength = computed(() => {
  return scorePassword(String(props.text));
});

const titleClass = computed(() => {
  return props.title ? 'password-meter--with-title' : '';
});
</script>

<template>
  <div
    :class="[
      `password-meter--lvl-${passwordStrength}`,
      titleClass,
      { 'password-meter--no-input': !props.text || props.text?.length < 1 },
    ]"
    :title="i18nGetPasswordLabel"
    class="password-meter w-full max-w-[50px] cursor-help"
  >
    <div class="password-meter__inner relative">
      <div class="password-meter__fill absolute" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
$levels: ((0, $danger, 5%), (1, $danger, 25%), (2, $warning, 50%), (3, $warning, 75%), (4, $success, 100%));

.password-meter {
  &:before {
    @apply absolute right-full top-[-7px] mr-[10px] hidden w-auto whitespace-nowrap text-sm content-[attr(title)];
    color: inherit;
  }

  &--with-title:before {
    @apply block;
  }

  &__inner {
    @apply top-px h-[5px] overflow-hidden rounded-[3px] bg-gray-10;
  }

  &--no-input &__fill {
    @apply w-0;
  }

  &--no-input:before {
    @apply hidden;
  }

  @each $level, $color, $width in $levels {
    &--lvl-#{$level} &__fill {
      background-color: $color;
      width: $width;
    }

    &--lvl-#{$level}:before {
      color: $color;
    }
  }

  &__fill {
    @apply bottom-0 left-0 top-0;
    transition: width 0.25s ease-in;
  }
}
</style>
